import type {IPortalSettings} from '../../../checkout/i';
import {isNullOrWhiteSpace} from '../../../util';
import type {ProductState, ProductUI, StoreInfo, ViewProduct} from '../../i';
import {isFixedSizeDisplay} from '../doc-sizes-and-categories/document-sizing';
import {degreeMarketing} from './degree-marketing';
import {getOverallFrameSize} from './overall-frame-size/overall-frame-size';

/**
 * What DescMainContent shows: the store's marketing HTML, the computed list and the store's override list.
 *
 * DF4 writes the same content into the product page's HTML for readers that run no scripts (PRODUCT-BULLETS-SSR.md):
 * ProductOpening.Description ports these rules line by line. description-content.test.ts records this module's output for the
 * real page fixtures in test-support/fixtures/description-content.json, and DF4.CheckoutTests checks the port against that file,
 * so a rule changed here fails the C# tests until the port follows.
 */

/** The link after a list line's text: the Level-Lock and SMARTbox modal buttons, and the Create-a-Frame link. */
export type DescriptionLink = 'level-lock' | 'smart-box' | 'create-a-frame';

/** One line of the computed list: its text and, after a space, its link. */
export type DescriptionBullet = {text: string; link?: DescriptionLink};

export type DescriptionContent = {
    /** si.default_prod_info_static; null when the section shows none. */
    marketing: string | null;
    /** The computed list; null when the section shows none (an empty list still renders its ul). */
    bullets: DescriptionBullet[] | null;
    /** si.default_prod_info_dynamic, the lines longer than 3 characters; null when the section shows none. */
    overrides: string[] | null;
};

/** What DescMainContent decides once, when it mounts. */
export type DescriptionFlags = {
    hideDynamic: boolean;
    showDocSize: boolean;
    showFrameSize: boolean;
    showCustomFrame: boolean;
    showSmartBox: boolean;
};

export type DescriptionPortal = Pick<IPortalSettings, 'PortalID' | 'DisableProdDescLinks'> | null | undefined;

export const NO_DESCRIPTION_FLAGS: DescriptionFlags = {hideDynamic: false, showDocSize: false, showFrameSize: false, showCustomFrame: false, showSmartBox: false};

// The stole and medallion frames, the gallery kit and one more item show no computed list.
const HIDE_DYNAMIC_PRODUCT_IDS = [70214, 70215, 70244, 84054, 98595];
const HIDE_DYNAMIC_ITEM_IDS = ['391332'];

// A value JSX prints as text: null and undefined print nothing.
const jsx = (value: string | null | undefined): string => value ?? '';

export const descriptionFlags = (prod: ViewProduct, ps: ProductState, ui: ProductUI): DescriptionFlags => {
    const hideDynamic = HIDE_DYNAMIC_PRODUCT_IDS.includes(prod.ProductID) || HIDE_DYNAMIC_ITEM_IDS.includes(prod.ItemID);
    return {
        hideDynamic,
        showDocSize: !isFixedSizeDisplay(prod) && ps.docWidth >= 0 && ps.docHeight >= 0 && !prod.Name.includes('collage') && prod.ProductTypeID != 25 && prod.IsFrame,
        showFrameSize: prod.JF_Width > 0,
        showCustomFrame: prod.IsFrame && prod.ProductTypeID !== 67,
        showSmartBox: !!ui.allow_smartbox && prod.Shipping_BoxID <= 3 && prod.IsFrame && !prod.ProductTypeDisplayName.toLowerCase().includes('Photo') && !hideDynamic,
    };
};

export const descriptionContent = (prod: ViewProduct, si: StoreInfo, ps: ProductState, ui: ProductUI, portal: DescriptionPortal, flags: DescriptionFlags, currentGradSchoolID: number): DescriptionContent => {
    const storeShows = si.StoreID !== 205255 && !si.has_bullet_override;
    const overridesShown = (!flags.hideDynamic || portal?.PortalID === 3) && si.has_bullet_override && si.default_prod_info_dynamic != null && si.default_prod_info_dynamic.length > 0;
    return {
        marketing: storeShows ? degreeMarketing(prod, si, ps, portal?.PortalID, currentGradSchoolID) : null,
        bullets: storeShows && !flags.hideDynamic ? descriptionBullets(prod, si, ps, ui, portal, flags) : null,
        overrides: overridesShown ? si.default_prod_info_dynamic.filter(m => m != null && m.length > 3) : null,
    };
};

/** The computed list, in the order the section has always rendered it. */
export const descriptionBullets = (prod: ViewProduct, si: StoreInfo, ps: ProductState, ui: ProductUI, portal: DescriptionPortal, flags: DescriptionFlags): DescriptionBullet[] => {
    const bullets: DescriptionBullet[] = [];
    const add = (text: string, link?: DescriptionLink) => bullets.push(link ? {text, link} : {text});
    const descLinks = portal?.PortalID === 0 || !portal?.DisableProdDescLinks;
    const noDescLinks = portal?.PortalID !== 0 || portal?.DisableProdDescLinks;

    const overall = flags.showFrameSize
        ? getOverallFrameSize({
              jfWidth: prod.JF_Width,
              jfHeight: prod.JF_Height,
              originalDocWidth: prod.docWidth,
              originalDocHeight: prod.docHeight,
              selectedDocWidth: ps.docWidth,
              selectedDocHeight: ps.docHeight,
              mouldingExteriorWidth: ps.moulding_extWidth,
              epId: ps.ep_id,
              epMethodId: ps.EPMethodID,
              epHeight: ps.EPHeight,
          })
        : null;
    const jf_width: number | false = overall === null ? false : overall.width;
    const jf_height: number | false = overall === null ? false : overall.height;

    if (!prod.IsCAF && (ps.use_plexi || prod.ForcePlexiglass)) add('Features premium acrylic glazing.');
    if (!prod.IsCAF && flags.showDocSize) add(`${si.ph_desc_size} ${ps.docWidth}"w x ${ps.docHeight}"h`);
    if (!prod.IsCAF && flags.showFrameSize) add(`${si.ph_desc_jf_size} ${jf_width}"w x ${jf_height}"h`);
    if (!prod.IsCAF && prod.LevelLock && descLinks && !prod.HideLevelLock) add(jsx(si.ph_desc_ll), 'level-lock');
    if (!prod.IsCAF && prod.LevelLock && noDescLinks && !prod.HideLevelLock) add(`${jsx(si.ph_desc_ll)} Level-Lock Frame Hanging System!`);
    if (!prod.IsCAF && flags.showSmartBox && descLinks) add(jsx(si.ph_desc_smart), 'smart-box');
    if (!prod.IsCAF && flags.showSmartBox && noDescLinks) add(`${jsx(si.ph_desc_smart)} SMARTbox package`);
    if (!prod.IsCAF && flags.showCustomFrame && si.StoreTypeID === 5) add('This custom frame is handcrafted in the USA.');
    if (!prod.IsCAF && flags.showCustomFrame && si.StoreTypeID !== 5) add(jsx(si.ph_desc_custom));
    if (!prod.IsCAF && prod.CustomizationMethod === 2 && portal?.PortalID === 0 && prod.ProductTypeID === 25) add('Customize your Varsity Letter frame with', 'create-a-frame');

    if (prod.IsCAF && ps.use_plexi) add('Features premium acrylic glazing.');
    if (prod.IsCAF && flags.showDocSize) add(`${prod.DocType} Size: ${ps.docWidth}"w x ${ps.docHeight}"h`);
    if (prod.IsCAF && flags.showFrameSize) add(`Approx. Overall Frame Size: ${jf_width}"w x ${jf_height}"h`);
    if (prod.IsCAF && prod.LevelLock && !prod.HideLevelLock && portal?.PortalID === 0) add('Includes our easy-to-use', 'level-lock');
    if (prod.IsCAF && flags.showSmartBox && portal?.PortalID === 0) add('This frame ships in a branded', 'smart-box');
    if (prod.IsCAF && flags.showCustomFrame && si.StoreTypeID === 5) add('This custom frame is handcrafted in the USA.');
    if (prod.IsCAF && flags.showCustomFrame && si.StoreTypeID !== 5) add('This custom frame is handcrafted in the USA and made-to-order.');
    if (!ps.is_caf && !isNullOrWhiteSpace(ui.item_id)) add(`Item #: ${ui.item_id}`);
    if (!ps.is_caf && !isNullOrWhiteSpace(ui.sku)) add(`SKU: ${ui.sku}`);
    if (prod.IsCAF && prod.DYF_FrameStyleID === 10 && ps.docWidth <= 7) add('Includes easel back');
    if (prod.IsCAF) add(jsx(prod.DYF_FrameStyleName));
    if (prod.IsCAF) add(`${jsx(ui.title_moulding)} Moulding`);
    if (prod.IsCAF) add(`${jsx(ui.title_mat1)} Top Mat`);
    if (prod.IsCAF && ps.mat2_id > 0) add(`${jsx(ui.title_mat2)} Inner Accent`);
    if (prod.IsCAF && ps.insignia_id > 0) add(`${jsx(ui.title_insignia)} Insignia`);
    if (ps.lino_id > 0) add(`${jsx(prod.linos.find(l => l.id === ps.lino_id)?.title || ps.lino_base_name)} Text.`);
    if (prod.IsCAF && ps.ep_id > 0) add('Personalized Plate');
    if (prod.IsCAF) add(`${ps.is_horizontal ? 'Horizontal' : 'Vertical'} Orientation`);
    if (prod.IsCAF && ps.glass_id > 1) add(`${jsx(ui.name_glass)} ${prod.AllowGlassID1 === 2 || ps.use_plexi ? 'Acrylic' : 'Glass'}`);

    return bullets;
};
